import React, { useState, useEffect } from 'react';

/**
 * @description Formulario para crear o editar un usuario.
 * En modo edición (cuando se proporciona `user`) el campo password no se muestra,
 * ya que el backend requiere un flujo separado para cambio de contraseña.
 * @param {object} props - Propiedades del componente.
 * @param {object | null} [props.user] - Usuario a editar. Si es null, se crea uno nuevo.
 * @param {boolean} [props.isSelf] - true si el usuario editado es el usuario autenticado
 *   (el rol queda bloqueado: un ADMIN no puede cambiar su propio rol).
 * @param {Function} props.onSave - Callback que recibe los datos del formulario al enviar.
 * @param {Function} props.onCancel - Callback que se ejecuta al cancelar.
 * @returns {JSX.Element}
 */
const UserForm = ({ user, isSelf = false, onSave, onCancel }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('TECHNICIAN');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [error, setError] = useState('');

  // Inicializar campos cuando se abre el formulario en modo edición
  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setEmail(user.email || '');
      setRole(user.role || 'TECHNICIAN');
      setPhone(user.phone || '');
      setPosition(user.position || '');
      setPassword(''); // La contraseña nunca se pre-carga
    } else {
      // Resetear todos los campos para el modo creación
      setUsername('');
      setEmail('');
      setPassword('');
      setRole('TECHNICIAN');
      setPhone('');
      setPosition('');
    }
    setError('');
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validación mínima en cliente
    if (!username.trim() || !email.trim()) {
      setError('El nombre de usuario y el email son obligatorios.');
      return;
    }
    if (!user && !password.trim()) {
      setError('La contraseña es obligatoria para crear un usuario.');
      return;
    }

    const data = { username: username.trim(), email: email.trim() };

    // En edición Users.js separa el rol y lo envía a PATCH /users/:id/role:
    // PATCH /users/:id no lo acepta (400 por forbidNonWhitelisted).
    // Si es el propio usuario no se envía, porque no puede cambiarlo.
    if (!isSelf) {
      data.role = role;
    }

    // Incluir phone solo si tiene valor para no enviar string vacío
    if (phone.trim()) {
      data.phone = phone.trim();
    }

    // Incluir position solo si tiene valor para no enviar string vacío
    if (position.trim()) {
      data.position = position.trim();
    }

    // La contraseña solo se envía en creación (POST /users)
    if (!user) {
      data.password = password;
    }

    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {/* Mostrar errores de validación del cliente */}
      {error && (
        <div style={styles.errorBox} role="alert">
          {error}
        </div>
      )}

      {/* Campo: nombre de usuario */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="user-username">Nombre de usuario</label>
        <input
          id="user-username"
          className="modal-field"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Ej: jperez"
          required
        />
      </div>

      {/* Campo: email */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="user-email">Email</label>
        <input
          id="user-email"
          type="email"
          className="modal-field"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Ej: jperez@empresa.com"
          required
        />
      </div>

      {/* Campo: contraseña — solo visible al crear un usuario nuevo */}
      {!user && (
        <div className="modal-field-group">
          <label className="modal-field-label" htmlFor="user-password">Contraseña</label>
          <input
            id="user-password"
            type="password"
            className="modal-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            required
          />
        </div>
      )}

      {/* Campo: rol — bloqueado solo cuando el ADMIN edita su propio usuario */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="user-role">Rol</label>
        <select
          id="user-role"
          className="modal-field"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={isSelf}
          aria-describedby={user ? 'user-role-hint' : undefined}
        >
          <option value="ADMIN">Admin</option>
          <option value="SUPERVISOR">Supervisor</option>
          <option value="TECHNICIAN">Técnico</option>
        </select>
        {user && (
          <span id="user-role-hint" style={styles.hint}>
            {isSelf
              ? 'No puedes cambiar tu propio rol.'
              : 'Las actividades ya registradas conservan el rol con el que se hicieron.'}
          </span>
        )}
      </div>

      {/* Campo: teléfono (opcional) */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="user-phone">Teléfono (opcional)</label>
        <input
          id="user-phone"
          className="modal-field"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Ej: +56912345678"
        />
      </div>

      {/* Campo: cargo o posición (opcional) */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="user-position">Cargo (opcional)</label>
        <input
          id="user-position"
          className="modal-field"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          placeholder="Ej: Técnico Eléctrico"
        />
      </div>

      <div className="modal-footer">
        <button type="button" onClick={onCancel} className="modal-btn-secondary">
          Cancelar
        </button>
        <button type="submit" className="modal-btn-primary">Guardar</button>
      </div>
    </form>
  );
};

// El gap entre campos vive acá porque Modal.js solo controla el espaciado
// de su único hijo directo (el <form>).
const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  // Caja de error — mismo lenguaje visual que Login.js/Register.js
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  // Texto de ayuda bajo un campo deshabilitado
  hint: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px',
  },
};

export default UserForm;
